// src/features/learner/components/Leaderboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';

// ---------------------------------------------------------------------------
// Leaderboard
//
// Props:
//   catalogId  — the current catalog; used to filter visitedPackages entries
//
// Data flow:
//   1. Fetch all learnerProgress docs (one per learner, ~60 total — cheap)
//   2. For each doc, count packages where:
//        entry.catalogId === props.catalogId
//        entry.visitedAt >= cutoff date (7 or 14 days ago)
//   3. Fetch display names from users collection only for learners with > 0
//   4. Rank and render
//
// Legacy-safe: array entries in visitedPackages (old format, no timestamp)
// are silently skipped — they simply won't appear in time-filtered counts.
// ---------------------------------------------------------------------------

const PERIODS = [
    { label: '7 days',  days: 7  },
    { label: '14 days', days: 14 },
];

const Leaderboard = ({ catalogId }) => {
    const { currentUser } = useAuth();

    const [period, setPeriod]       = useState(0); // index into PERIODS
    const [entries, setEntries]     = useState([]);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState(null);

    // ---------------------------------------------------------------------------
    // Build leaderboard entries for the selected time period
    // ---------------------------------------------------------------------------
    const buildLeaderboard = useCallback(async (periodDays) => {
        setLoading(true);
        setError(null);

        try {
            const cutoff = new Date();
            cutoff.setDate(cutoff.getDate() - periodDays);

            // 1. Fetch all learnerProgress docs — one collection scan, ~60 docs
            const progressSnap = await getDocs(collection(db, 'learnerProgress'));

            // 2. Count qualifying package visits per learner
            const counts = []; // [{ uid, count }]

            progressSnap.forEach(docSnap => {
                const data  = docSnap.data();
                const uid   = data.learnerId || docSnap.id;
                const raw   = data.visitedPackages;

                // Skip if no visitedPackages at all, or legacy array format
                // (array entries have no timestamp, can't be time-filtered)
                if (!raw || Array.isArray(raw)) return;

                let count = 0;
                Object.values(raw).forEach(entry => {
                    if (!entry || entry.catalogId !== catalogId) return;
                    if (!entry.visitedAt) return; // legacy migrated entry — no timestamp

                    // Firestore Timestamps have .toDate(); plain Dates are already Dates
                    const visited = entry.visitedAt?.toDate
                        ? entry.visitedAt.toDate()
                        : new Date(entry.visitedAt);

                    if (visited >= cutoff) count++;
                });

                // Rule: only show learners who completed at least one package
                if (count > 0) {
                    counts.push({ uid, count });
                }
            });

            if (counts.length === 0) {
                setEntries([]);
                setLoading(false);
                return;
            }

            // 3. Sort descending
            counts.sort((a, b) => b.count - a.count);

            // 4. Fetch display names only for learners who appear in the list
            const uids = counts.map(c => c.uid);
            const userDocs = await Promise.all(
                uids.map(uid => getDoc(doc(db, 'users', uid)))
            );

            const nameMap = {};
            userDocs.forEach((snap, i) => {
                nameMap[uids[i]] = snap.exists()
                    ? (snap.data().name || snap.data().email || 'Learner')
                    : 'Learner';
            });

            // 5. Assign ranks (tied learners share a rank)
            const ranked = [];
            let rank = 1;
            counts.forEach((entry, i) => {
                if (i > 0 && entry.count < counts[i - 1].count) rank = i + 1;
                ranked.push({
                    rank,
                    uid:   entry.uid,
                    name:  nameMap[entry.uid],
                    count: entry.count,
                    isMe:  entry.uid === currentUser?.uid,
                });
            });

            setEntries(ranked);
        } catch (err) {
            console.error('Leaderboard fetch error:', err);
            setError('Could not load leaderboard.');
        } finally {
            setLoading(false);
        }
    }, [catalogId, currentUser?.uid]);

    useEffect(() => {
        if (catalogId) buildLeaderboard(PERIODS[period].days);
    }, [catalogId, period, buildLeaderboard]);

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    const medal = (rank) => {
        if (rank === 1) return '🥇';
        if (rank === 2) return '🥈';
        if (rank === 3) return '🥉';
        return null;
    };

    return (
        <div style={{
            marginBottom: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.07)'
        }}>
            {/* Header row */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: '1px solid #f3f4f6',
                backgroundColor: '#f9fafb'
            }}>
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                    🏆 Leaderboard
                </span>

                {/* Period toggle */}
                <div style={{ display: 'flex', gap: '4px' }}>
                    {PERIODS.map((p, i) => (
                        <button
                            key={p.label}
                            onClick={() => setPeriod(i)}
                            style={{
                                padding: '4px 10px',
                                borderRadius: '20px',
                                border: 'none',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                backgroundColor: period === i ? '#19a4a0' : '#e5e7eb',
                                color: period === i ? '#ffffff' : '#6b7280',
                                transition: 'background-color 0.15s ease'
                            }}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Body */}
            <div style={{ padding: '8px 0' }}>
                {loading && (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                        Loading…
                    </div>
                )}

                {!loading && error && (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#ef4444', fontSize: '0.85rem' }}>
                        {error}
                    </div>
                )}

                {!loading && !error && entries.length === 0 && (
                    <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                        No activity in the last {PERIODS[period].days} days yet.
                    </div>
                )}

                {!loading && !error && entries.map((entry, i) => (
                    <div
                        key={entry.uid}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 16px',
                            backgroundColor: entry.isMe ? '#f0fdfc' : 'transparent',
                            borderLeft: entry.isMe ? '3px solid #19a4a0' : '3px solid transparent',
                            borderBottom: i < entries.length - 1 ? '1px solid #f3f4f6' : 'none',
                        }}
                    >
                        {/* Rank */}
                        <span style={{
                            width: '28px',
                            flexShrink: 0,
                            textAlign: 'center',
                            fontSize: medal(entry.rank) ? '1.1rem' : '0.8rem',
                            fontWeight: 700,
                            color: '#6b7280'
                        }}>
                            {medal(entry.rank) || `#${entry.rank}`}
                        </span>

                        {/* Name */}
                        <span style={{
                            flex: 1,
                            fontSize: '0.9rem',
                            fontWeight: entry.isMe ? 700 : 500,
                            color: entry.isMe ? '#19a4a0' : '#111827',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {entry.name}{entry.isMe ? ' (you)' : ''}
                        </span>

                        {/* Package count */}
                        <span style={{
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: '#6b7280',
                            flexShrink: 0
                        }}>
                            {entry.count} {entry.count === 1 ? 'unit' : 'units'}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Leaderboard;